import { useMemo } from 'react';
import { useActiveSearch } from '@/lib/active-search';
import { useClaims } from '../hooks/use-claims';
import { useClaimsView } from '../hooks/use-claims-view';
import { useClaimsWorkspace } from '../hooks/use-claims-workspace';
import { claimDateBounds } from '../utils/claim-date-bounds';

import { ClaimDetailPanel } from './ClaimDetailPanel';
import { ClaimsPlaceholder, claimsSectionState } from './ClaimsPlaceholder';
import { ClaimsFilterPanel } from './ClaimsFilterPanel';
import { ClaimsGrid } from './ClaimsGrid';
import { ClaimsToolbar } from './ClaimsToolbar';
import { TruncationNotice } from './TruncationNotice';
import { ClaimsPagination } from './ClaimsPagination';

const NO_MATCHES_MESSAGE = 'No claims match your search or filters.';

/** Claims section (US-009…US-015). Renders directly below Policyholder Information, on the same page. */
export function ClaimsSection(): React.JSX.Element | null {
  const { data: criteria } = useActiveSearch();
  const { data, isPending, isError, error, fetchStatus } = useClaims();
  const policyNumber = criteria?.policyNumber ?? '';
  const workspace = useClaimsWorkspace(policyNumber);

  const claims = useMemo(() => data?.claims ?? [], [data]);
  const truncated = data?.truncated === true;
  const view = useClaimsView(claims, policyNumber);

  const resetAll = (): void => {
    workspace.clearFilterDraft();
    view.resetView();
  };
  // Derived from the claims themselves: a day outside what they cover cannot match a row, so the
  // pickers are bounded to it and a value beyond it is reported rather than silently returning nothing.
  const dateBounds = useMemo(() => claimDateBounds(claims), [claims]);
  const statuses = useMemo(
    () =>
      [
        ...new Set(claims.map((claim) => claim.status).filter((s): s is string => s !== null)),
      ].sort(),
    [claims],
  );

  const state = claimsSectionState({
    hasSearch: criteria !== null && criteria !== undefined,
    isPending,
    isIdle: fetchStatus === 'idle',
    // Claims already retrieved stay on screen when a later call fails (PP-028 AC-003).
    isError: isError && data === undefined,
    claimCount: claims.length,
  });

  if (state !== 'ready') {
    return <ClaimsPlaceholder state={state} error={error} errorStatus={error?.status} />;
  }

  return (
    <section
      aria-labelledby="claims-heading"
      className="border-line rounded-xl border bg-surface p-6 shadow-sm"
    >
      <ClaimsToolbar
        keyword={view.keyword}
        filterOpen={workspace.filterOpen}
        resetEnabled={view.viewChanged}
        onKeywordChange={view.setKeyword}
        onToggleFilter={workspace.toggleFilterPanel}
        onReset={resetAll}
      />

      {truncated && <TruncationNotice shown={claims.length} total={data.totalCount} />}

      <div className="mt-4">
        {workspace.filterOpen && (
          <ClaimsFilterPanel
            draft={workspace.filterDraft}
            statuses={statuses}
            dateBounds={dateBounds}
            onDraftChange={workspace.setFilterDraft}
            onApply={view.applyFilters}
            onReset={resetAll}
            onClose={workspace.closeFilterPanel}
          />
        )}

        {view.totalMatches === 0 ? (
          <p role="status" className="text-muted py-10 text-center text-sm">
            {NO_MATCHES_MESSAGE}
          </p>
        ) : (
          <>
            <ClaimsGrid
              rows={view.rows}
              sort={view.sort}
              selectedClaimNumber={workspace.selected?.claimNumber ?? null}
              linesOf={view.linesOf}
              onToggleSort={view.toggleSort}
              onSelect={workspace.select}
            />
            <ClaimsPagination
              page={view.page}
              pageCount={view.pageCount}
              totalMatches={view.totalMatches}
              onGoToPage={view.goToPage}
            />
          </>
        )}

        {workspace.selected !== null && (
          <ClaimDetailPanel
            claim={workspace.selected}
            onClose={(): void => {
              workspace.select(null);
            }}
          />
        )}
      </div>
    </section>
  );
}
